import {useContext, useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'

import PropTypes from 'prop-types'

import {htmlStringToReactElement} from '@s-ui/js/lib/react'
import AtomButton, {atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import {ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import Context from '@s-ui/react-context'
import MoleculeNotification, {
  moleculeNotificationAlignItems,
  moleculeNotificationAutoClose,
  moleculeNotificationBorderSizes,
  moleculeNotificationTypes,
  moleculeNotificationVariations
} from '@s-ui/react-molecule-notification'
import CircleCheckOutline from '@adv-mt/mt-svg-icons/lib/CircleCheckOutline'
import CircleClose from '@adv-mt/mt-svg-icons/lib/CircleClose'

const STATUS_ERROR = 'error'
const STATUS_SUCCESS = 'success'
const STATUS_IN_PROGRESS = 'inProgress'
const STATUS_FINISHED = 'finished'
const MAX_TIMER = 180_000
const TIMER_INTERVAL = 1_000
const MAX_RETRIES = 20
const RETRY_INTERVAL = 1000

const notificationData = {
  error: {Icon: CircleClose, text: 'RESERVATION_C2B.USER_FORM.ERROR_NOTIFICATION', type: 'error'},
  success: {Icon: CircleCheckOutline, text: 'RESERVATION_C2B.USER_FORM.SUCCESS_NOTIFICATION', type: 'success'}
}

const useTimer = ({setTimerStatus}) => {
  const [timer, setTimer] = useState(MAX_TIMER)
  const ref = useRef(null)

  const clearTimer = () => {
    if (ref.current) clearInterval(ref.current)
  }

  const resetTimer = () => {
    clearTimer()
    setTimerStatus(STATUS_FINISHED)
    ref.current = null
    setTimer(MAX_TIMER)
  }

  const initTimer = () => {
    if (!ref.current) {
      const id = setInterval(() => {
        setTimer(prevTimer => {
          if (prevTimer > 0) return prevTimer - TIMER_INTERVAL
          resetTimer()
          return 0
        })
      }, TIMER_INTERVAL)
      ref.current = id
    }
  }

  return {timer, initTimer}
}

export default function VerificationReminder({adId, currentUser, onContinue}) {
  const {domain, i18n} = useContext(Context)

  const [notificationStatus, setNotificationStatus] = useState(null)
  const [timerStatus, setTimerStatus] = useState('')
  const [isUserVerified, setIsUserVerified] = useState(false)

  const {timer, initTimer} = useTimer({setTimerStatus})

  const formatTimer = s => new Date(s).toISOString().substr(14, 5)

  const wait = time => new Promise(resolve => setTimeout(resolve, time))

  const recursiveRetrieveUserData = async ({isCurrentUserVerified = false, currentRetry = 0}) => {
    if (!isCurrentUserVerified && currentRetry < MAX_RETRIES) {
      await wait(RETRY_INTERVAL * currentRetry)
      const [error, userDataRes] = await domain.get('get_user_by_id_user_use_case').execute()
      if (error) console.error(error) // eslint-disable-line no-console
      return recursiveRetrieveUserData({
        isCurrentUserVerified: userDataRes?.isEmailConfirmed,
        currentRetry: currentRetry + 1
      })
    }
    if (isCurrentUserVerified) setIsUserVerified(true)
  }

  const buildRedirectUri = () => {
    if (!adId) return undefined
    const config = domain.get('config')
    const COMPANY_URL = config.get('COMPANY_URL')
    const {RESERVATION_PAYMENT_PATH_PATTERN} = config.get('page')
    const params = new URLSearchParams({
      adId,
      utm_medium: 'email',
      utm_source: 'system',
      utm_campaign: 'firstcontact',
      utm_content: 'reservationbutton'
    })
    return `${COMPANY_URL}${RESERVATION_PAYMENT_PATH_PATTERN}?${params.toString()}`
  }

  const sendEmailToVerifyUser = async () => {
    const [error, res] = await domain
      .get('send_email_to_verify_user_use_case')
      .execute({redirectUri: buildRedirectUri()})
    if (error) setNotificationStatus(STATUS_ERROR)
    if (res) {
      setNotificationStatus(STATUS_SUCCESS)
      setTimerStatus(STATUS_IN_PROGRESS)
      initTimer()
    }
  }

  useEffect(() => {
    sendEmailToVerifyUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (timerStatus === STATUS_IN_PROGRESS) recursiveRetrieveUserData({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerStatus])

  useEffect(() => {
    if (isUserVerified) onContinue()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserVerified])

  const showNotification = notificationStatus !== null
  const {Icon, text, type} = (notificationStatus !== null && notificationData[notificationStatus]) || {}
  const disabledButtonText = i18n.t('USER_PROFILE.EMAIL_VALIDATION.RESEND_EMAIL_AGAIN', {
    timer: formatTimer(timer)
  })

  return (
    <div className="mt-LoginModal-verificationReminderTextContainer">
      <p className="mt-LoginModal-verificationReminderText">
        {htmlStringToReactElement(
          i18n.t('USER_PROFILE.EMAIL_VALIDATION.RESERVATION_C2B.DESCRIPTION', {
            strongStart: '<strong>',
            strongEnd: '</strong>'
          })
        )}
      </p>
      {currentUser?.email && (
        <p className="mt-LoginModal-verificationReminderText">
          {htmlStringToReactElement(
            i18n.t('USER_PROFILE.EMAIL_VALIDATION.CHECK_INBOX', {
              strongStart: '<strong>',
              userEmail: currentUser.email,
              strongEnd: '</strong>'
            })
          )}
        </p>
      )}
      <div className="mt-LoginModal-verificationReminderButton">
        <AtomButton
          disabled={timerStatus === STATUS_IN_PROGRESS}
          fullWidth
          onClick={sendEmailToVerifyUser}
          shape={atomButtonShapes.CIRCULAR}
          size={atomButtonSizes.LARGE}
        >
          {timerStatus === STATUS_IN_PROGRESS ? disabledButtonText : i18n.t('COMMON.RESEND_EMAIL')}
        </AtomButton>
      </div>
      {showNotification &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="mt-LoginModal-verificationReminderNotification">
            <MoleculeNotification
              alignItems={moleculeNotificationAlignItems.CENTER}
              autoClose={moleculeNotificationAutoClose.manual}
              icon={<Icon size={ATOM_ICON_SIZES.medium} />}
              roundedCorners={moleculeNotificationBorderSizes.medium}
              showCloseButton={false}
              showLeftIconMobile
              type={moleculeNotificationTypes[type]}
              variation={moleculeNotificationVariations.negative}
            >
              <span>{i18n.t(text)}</span>
            </MoleculeNotification>
          </div>,
          document.body
        )}
    </div>
  )
}

VerificationReminder.displayName = 'VerificationReminder'
VerificationReminder.propTypes = {
  adId: PropTypes.string,
  currentUser: PropTypes.object,
  onContinue: PropTypes.func.isRequired
}
